'use client';

import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { Timer } from 'lucide-react';

export function HardcoreTimer({
  seconds,
  label,
  frozen = false,
  won = false,
}: {
  seconds: number;
  label?: string;
  /** Chrono terminé : figé (00:00), sans pulse, laser ralenti et atténué. */
  frozen?: boolean;
  /** Mot réussi : code couleur vert (laser + fond + texte). */
  won?: boolean;
}) {
  const safeSeconds = Math.max(0, seconds);
  const urgent = !frozen && safeSeconds <= 10;
  const mm = Math.floor(safeSeconds / 60);
  const ss = String(safeSeconds % 60).padStart(2, '0');

  return (
    <motion.div
      role="timer"
      aria-label={label}
      initial={{ opacity: 0, y: -8 }}
      animate={
        urgent
          ? { opacity: 1, y: 0, scale: [1, 1.08, 1] }
          : { opacity: 1, y: 0, scale: 1 }
      }
      transition={
        urgent
          ? { scale: { duration: 0.6, repeat: Infinity, ease: 'easeInOut' } }
          : { duration: 0.2 }
      }
      style={
        {
          '--laser-width': urgent ? '3px' : '2.5px',
          '--laser-speed': frozen ? '4s' : urgent ? '1.1s' : '2.6s',
        } as CSSProperties
      }
      className={`laser-ring ${
        won ? 'laser-ring-won ' : ''
      }flex items-center gap-2 rounded-full px-4 py-2 font-mono text-xl font-bold tabular-nums tracking-widest md:gap-2.5 md:px-5 md:py-2.5 md:text-2xl ${
        won
          ? 'bg-emerald-950/50 text-emerald-200 shadow-[0_0_14px_rgba(16,185,129,0.4)]'
          : frozen
          ? 'bg-red-950/40 text-red-300/80 shadow-[0_0_10px_rgba(239,68,68,0.25)]'
          : urgent
          ? 'bg-red-950/70 text-red-200 shadow-[0_0_22px_rgba(239,68,68,0.65)]'
          : 'bg-red-950/40 text-red-200 shadow-[0_0_12px_rgba(239,68,68,0.35)]'
      }`}
    >
      <Timer className="h-5 w-5 md:h-6 md:w-6" />
      <span>
        {mm}:{ss}
      </span>
    </motion.div>
  );
}
